import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Dimensions,
  TouchableWithoutFeedback,
} from 'react-native';
import { SVG_COMPONENTS, SVG_DIMENSIONS } from '@/assets/admin';
import { IOrganization, IProperty } from '@/types/admin/navigation';
import { EnhancedSelector } from '../../common/admin/mobile/Selector';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface PropertyOrgSelectorsProps {
  organizations: IOrganization[];
  properties: IProperty[];
  selectedOrganizationId?: number;
  selectedPropertyId?: number;
  onOrganizationChange: (orgId: number) => void;
  onPropertyChange: (propId: number) => void;
  showPropertySelector?: boolean;
  // When true, the Organization selector is disabled (matches admin-web gating)
  disableOrganization?: boolean;
  disabled?: boolean;
}

export const PropertyOrgSelectors: React.FC<PropertyOrgSelectorsProps> = ({
  organizations,
  properties,
  selectedOrganizationId,
  selectedPropertyId,
  onOrganizationChange,
  onPropertyChange,
  showPropertySelector = true,
  disableOrganization = false,
  disabled = false,
}) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const selectedOrg = organizations.find(org => org.id === selectedOrganizationId);
  const selectedProp = properties.find(prop => prop.id === selectedPropertyId);
  
  // Filter properties by selected organization
  const filteredProperties = properties.filter(
    prop => prop.organizationId === selectedOrganizationId
  );

  const handleOrganizationChange = useCallback((orgId: number) => {
    onOrganizationChange(orgId);
    
    // Auto-select first available property for the new organization
    const availableProperty = properties.find(prop => prop.organizationId === orgId);
    if (availableProperty) {
      onPropertyChange(availableProperty.id);
    }
  }, [onOrganizationChange, onPropertyChange, properties]);

  // Close all dropdowns when tapping outside
  const closeAllDropdowns = useCallback(() => {
    setActiveDropdown(null);
  }, []);

  // Don't show organization selector if there's only one organization
  const showOrganizationSelector = organizations.length > 1;
  
  // Show property selector if there's at least 1 property for the selected org
  const showPropertySelectorForOrg = filteredProperties.length >= 1 && showPropertySelector;
  // Disable property selector if there's only 1 property (nothing to select)
  const disablePropertySelector = filteredProperties.length <= 1 || disabled;

  return (
    <TouchableWithoutFeedback onPress={closeAllDropdowns}>
      <View style={styles.container}>
        {showOrganizationSelector && (
          <EnhancedSelector
            label="Selected Organization"
            value={selectedOrg?.name || ''}
            options={organizations.map(org => ({ id: org.id, name: org.name }))}
            onSelect={handleOrganizationChange}
            disabled={disabled || disableOrganization}
            isActive={activeDropdown === 'organization'}
            onToggle={(isOpen) => setActiveDropdown(isOpen ? 'organization' : null)}
          />
        )}
        
        {showPropertySelectorForOrg && (
          <EnhancedSelector
            label="Selected Property"
            value={selectedProp?.marketingName || ''}
            options={filteredProperties.map(prop => ({ id: prop.id, name: prop.marketingName }))}
            onSelect={onPropertyChange}
            disabled={disablePropertySelector}
            isActive={activeDropdown === 'property'}
            onToggle={(isOpen) => setActiveDropdown(isOpen ? 'property' : null)}
          />
        )}
      </View>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    gap: 20,
    // Ensure dropdowns can render outside container bounds
    overflow: 'visible',
  },
});
